import {css, StyleSheet} from "aphrodite";
import {useEffect, useId, useRef, useState} from "react";

import {Text, View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {doNotTranslateYet} from "@khan/i18n";

import CriteriaList from "./criteria-list.tsx";
import ScreenReaderAlert, {
    type ScreenReaderAlertRefHandle,
} from "./screen-reader-alert.tsx";
import * as strings from "./strings.tsx";
import {checkFilenameCriteria, pythonFileExtension} from "./utils.ts";

import checkIcon from "@phosphor-icons/core/bold/check-bold.svg";
import warningIcon from "@phosphor-icons/core/regular/warning-circle.svg";

type Props = {
    // The name of the file being renamed, if applicable.
    currentFilename?: string;
    // The set of filenames in the editor.
    filenames: Set<string>;
    // Called when the filename entry is cancelled. The second parameter is the
    // blur event that caused the form to be exited, if the exit was caused by
    // the user unfocusing the form.
    onCancel: (blurEvent: React.FocusEvent | null) => void;
    // Called when a filename is confirmed. The second parameter is the blur
    // event that caused the form to be submitted, if the submission was caused
    // by the user unfocusing the form.
    onSubmit: (newName: string, blurEvent: React.FocusEvent | null) => void;
    // True if the file that is being named is selected in the editor.
    isSelected: boolean;
};

export default function FilenameEntryForm({
    currentFilename,
    filenames,
    onCancel,
    onSubmit,
    isSelected,
}: Props): React.ReactElement {
    const inputId = useId();

    const [editedFilenameWithoutExtension, setEditedFilenameWithoutExtension] =
        useState(
            currentFilename?.substring(
                0,
                currentFilename.length - pythonFileExtension.length,
            ) ?? "",
        );
    const editedFilename = `${editedFilenameWithoutExtension}${pythonFileExtension}`;

    const [hasSubmittedInvalidFilename, setHasSubmittedInvalidFilename] =
        useState(false);

    const inputRef = useRef<HTMLInputElement | null>(null);
    const alertRef = useRef<ScreenReaderAlertRefHandle | null>(null);

    const filenameCriteria = checkFilenameCriteria(
        editedFilenameWithoutExtension,
        currentFilename,
        filenames,
    );
    const isValidFilename = filenameCriteria.every((c) => c.isSatisfied);

    const shouldUseErrorStyles =
        hasSubmittedInvalidFilename && !isValidFilename;

    const filenameCriteriaNode = <CriteriaList criteria={filenameCriteria} />;

    const isCompletedRef = useRef(false);
    const forceComplete = (blurEvent: React.FocusEvent | null) => {
        if (isCompletedRef.current) {
            return;
        }
        isCompletedRef.current = true;

        if (isValidFilename && editedFilename !== currentFilename) {
            onSubmit(editedFilename, blurEvent);
        } else {
            onCancel(blurEvent);
        }
    };

    const forceCompleteRef = useRef(forceComplete);
    forceCompleteRef.current = forceComplete;
    useEffect(() => () => forceCompleteRef.current(null), []);

    const onFormSubmit = () => {
        if (editedFilename === currentFilename) {
            onCancel(null);
            isCompletedRef.current = true;
        } else if (isValidFilename) {
            onSubmit(editedFilename, null);
            isCompletedRef.current = true;
        } else {
            setHasSubmittedInvalidFilename(true);
            inputRef.current?.focus();
            alertRef.current?.forceAlert();
        }
    };

    return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- TODO: Address a11y error
        <form
            onSubmit={(e) => {
                e.preventDefault();
                onFormSubmit();
            }}
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    forceComplete(e);
                }
            }}
            className={css(styles.form)}
        >
            <label className="sr-only" htmlFor={inputId}>
                {currentFilename == null
                    ? doNotTranslateYet`Name of new file`
                    : strings.renameFileInputLabel(currentFilename)}
            </label>
            <View>
                <Text style={styles.invisibleTextForInputSizing}>
                    {
                        // this text is used to size the input.
                        // it is invisible to the user, and has the same font
                        // styles as the input. when it grows, the View grows
                        // to accommodate it, and the input fills the view.
                    }
                    {editedFilenameWithoutExtension}
                    {pythonFileExtension}
                </Text>
                <Tooltip
                    opened={true}
                    forceAnchorFocusivity={false}
                    content={filenameCriteriaNode}
                >
                    <input
                        autoFocus
                        id={inputId}
                        type="text"
                        className={css(
                            styles.input,
                            !isSelected && styles.inputUnselected,
                            shouldUseErrorStyles && styles.inputError,
                        )}
                        value={editedFilenameWithoutExtension}
                        size={1}
                        onChange={(e) =>
                            setEditedFilenameWithoutExtension(e.target.value)
                        }
                        ref={inputRef}
                    />
                </Tooltip>
            </View>
            <View style={styles.inputDecoration}>
                <Text style={styles.fileExtension}>{pythonFileExtension}</Text>
                <ScreenReaderAlert ref={alertRef}>
                    {filenameCriteriaNode}
                </ScreenReaderAlert>
                <PhosphorIcon
                    color={color.red}
                    icon={warningIcon}
                    style={!shouldUseErrorStyles && styles.invisible}
                    size="small"
                    role="img"
                    aria-label={doNotTranslateYet`Invalid name entered`}
                />
            </View>
            {!shouldUseErrorStyles && (
                <IconButton
                    aria-label={doNotTranslateYet`Submit`}
                    icon={checkIcon}
                    kind="tertiary"
                    style={styles.submitButton}
                    size="xsmall"
                    onClick={onFormSubmit}
                />
            )}
        </form>
    );
}

const inputTextStyles = {
    fontFamily: "Lato, 'Noto Sans', sans-serif",
    fontSize: 14,
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
};

const horizontalGapSize = spacing.xxSmall_6;
const iconSize = spacing.medium_16;

const styles = StyleSheet.create({
    form: {
        position: "relative",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 0,
        height: 22,
    },
    input: {
        height: "100%",
        borderRadius: 4,
        boxSizing: "border-box",
        border: `1px solid ${color.offBlack50}`,
        paddingLeft: horizontalGapSize - 1,
        ...inputTextStyles,
        ":focus": {
            outline: "none",
            border: `2px solid ${color.blue}`,
            paddingLeft: horizontalGapSize - 2,
        },
    },
    inputUnselected: {
        backgroundColor: color.offBlack8,
    },
    inputError: {
        backgroundColor: "#FDE3E0",
        ":focus": {
            borderColor: color.red,
        },
    },
    invisibleTextForInputSizing: {
        visibility: "hidden",
        height: 0,
        paddingLeft: horizontalGapSize,
        paddingRight: iconSize + 2 * horizontalGapSize,
        ...inputTextStyles,
    },
    inputDecoration: {
        position: "absolute",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        right: 0,
        top: 0,
        bottom: 0,
        paddingRight: horizontalGapSize,
        pointerEvents: "none",
    },
    fileExtension: {
        ...inputTextStyles,
        marginRight: horizontalGapSize,
    },
    invisible: {
        visibility: "hidden",
    },
    submitButton: {
        color: color.offBlack,
        width: iconSize,
        height: iconSize,
        position: "absolute",
        right: horizontalGapSize,
    },
});
